import React, { useState, useEffect } from 'react'
import { Icon, parsers } from 'roadie-ui'
import { Container, Text, Spacer, Icon as RoadieUIIcon, Modal, Button } from 'roadie-ui'
import DriveSidebar from 'components/layout/DriveSidebar'
import Helmet from 'react-helmet'
import Page from 'rui/widgets/Page'
import Panel, { PanelTitle } from 'components/layout/Panel'
import { BlockPayment, PAYMENT_CATEGORY_MAP, PAYMENT_STATUS_MAP, fetchBlockPaymentData } from 'data/blockPayments'
import { fetchConsolidationDetailsBulk } from 'actions/gigs'
import { MapDisplay } from 'components/pages/MapDisplay'
import { Consolidation, TransactionCell } from './TransactionCell'
import SupportFooter from './SupportFooter'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

type BlockDetailsSectionProps = {
  blockId: number
}

const BlockDetailsSection: React.FC<BlockDetailsSectionProps> = ({ blockId }) => {
  const { t } = useLocalization()
  const [blockPaymentData, setBlockPaymentData] = useState<BlockPayment | null>(null)
  const [consolidations, setConsolidations] = useState<Consolidation[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [consolidationsToShowIndex, setConsolidationsToShowIndex] = useState(5)

  // Helper function to format time in user's timezone as "9:00 AM"
  const formatTimeForUser = (date: Date) => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return date.toLocaleString('en-US', {
      timeZone: userTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  useEffect(() => {
    fetchBlockPaymentData(blockId)
      .then((data) => {
        setBlockPaymentData(data)
        return fetchConsolidationDetailsBulk(data.consolidationIds)
      })
      .then((response: { consolidations: Consolidation[] }) => {
        setConsolidations(response.consolidations || [])
      })
      .catch((error: Error) => {
        console.error('Error fetching consolidation details:', error)
      })
  }, [blockId])

  if (!blockPaymentData) {
    return null
  }

  const startTime = blockPaymentData.scheduledStartTime
  const endTime = blockPaymentData.scheduledEndTime
  const driverStartTime = blockPaymentData.driverStartTime
  const driverEndTime = blockPaymentData.driverEndTime

  // Create dictionary mapping payment categories to total amounts
  const paymentCategoryTotals = blockPaymentData.payments
    .filter((payment) => payment.paymentStatus !== 'cancelled')
    .reduce(
      (acc, payment) => {
        acc[payment.paymentCategory] = (acc[payment.paymentCategory] || 0) + payment.paymentAmount
        return acc
      },
      {} as Record<string, number>
    )

  // Check if any reconciliation payments are held (AKA "pending")
  const reconciliationPaymentsHeld = blockPaymentData.payments
    .filter((payment) => payment.paymentStatus !== 'cancelled')
    .filter((payment) => payment.paymentCategory === PAYMENT_CATEGORY_MAP.reconciliation)
    .some((payment) => payment.paymentStatus === PAYMENT_STATUS_MAP.held)

  const guaranteedPay =
    (paymentCategoryTotals[PAYMENT_CATEGORY_MAP.gigPay] || 0) +
    (paymentCategoryTotals[PAYMENT_CATEGORY_MAP.reconciliation] || 0)

  const totalAmount = blockPaymentData
    ? (paymentCategoryTotals[PAYMENT_CATEGORY_MAP.gigPay] || 0) +
      (!reconciliationPaymentsHeld ? paymentCategoryTotals[PAYMENT_CATEGORY_MAP.reconciliation] || 0 : 0) +
      (paymentCategoryTotals[PAYMENT_CATEGORY_MAP.tip] || 0) +
      (paymentCategoryTotals[PAYMENT_CATEGORY_MAP.reimbursement] || 0)
    : 0

  return (
    <>
      <Panel left>
        <Container.Base>
          <MapDisplay consolidations={consolidations} />

          <Spacer $height="2.4rem" />

          <Text.H4 $color="neutral-black">
            {t('dweb_common_block')}
            {startTime && endTime && ` ${formatTimeForUser(startTime)} - ${formatTimeForUser(endTime)}`}
          </Text.H4>

          <Spacer $height="1.6rem" />

          <Container.Grid $gridTemplateColumns="1.8rem .8rem max-content">
            <RoadieUIIcon.Clock />
            <Spacer $width=".8rem" />
            <Text.Body2>
              <LocalizedRichText
                translationKey="dweb_block_payments_block_details_duration_value1_value2_value3"
                params={{
                  value1: formatTimeForUser(driverStartTime),
                  value2: formatTimeForUser(driverEndTime),
                  value3: driverEndTime.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }),
                }}
              />
            </Text.Body2>
          </Container.Grid>

          <Spacer $height="1.6rem" />

          <Container.Grid $gridTemplateColumns="1.8rem .8rem max-content">
            <RoadieUIIcon.Package />
            <Spacer $width=".8rem" />
            <Text.Body2>
              {blockPaymentData.consolidationIds.length}{' '}
              {blockPaymentData.consolidationIds.length === 1 ? 'Gig' : 'Gigs'}
            </Text.Body2>
          </Container.Grid>

          <Spacer $height="3.2rem" />

          <Container.Grid $gridGap="1.6rem">
            <Container.Grid
              $borderColor={parsers.borderColor.modify('neutral-dark', parsers.baselineColor.modifiers.opacity(0.3))}
              $borderWidth="1px"
              $borderStyle="solid"
              $borderRadius=".4rem"
            >
              {/* Block Guaranteed Pay section */}
              <Container.Grid
                $gridTemplateColumns="3fr 1fr"
                $backgroundColor={parsers.surfaceColor.modify(
                  'neutral-light',
                  parsers.baselineColor.modifiers.opacity(0.3)
                )}
                $padding="1.6rem"
              >
                <Text.Body1 $color="neutral-black">
                  <LocalizedText translationKey="dweb_block_payments_block_details_block_guaranteed_pay" />
                </Text.Body1>
                <Text.Body1 $color="neutral-black" $textAlign="right">
                  ${guaranteedPay.toFixed(2)}
                </Text.Body1>
              </Container.Grid>

              {/* Payment details section */}
              <Container.Grid
                $gridTemplateColumns="3fr 1fr"
                $backgroundColor="neutral-white"
                $padding="1.6rem"
                $color="neutral-black"
              >
                <Container.Grid $padding="0 0 0 1.6rem">
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_common_gig_pay" />
                  </Text.Body2>
                  <Container.Grid $display="flex" $alignItems="center">
                    <Text.Body2 style={{ marginRight: '0.4rem' }}>
                      <LocalizedText translationKey="dweb_block_payments_block_details_pay_adjustment" />
                    </Text.Body2>
                    <Button.Tertiary
                      type="button"
                      onClick={() => setIsModalOpen(true)}
                      style={{ padding: 0, minWidth: 0 }}
                    >
                      <Icon.QuestionMarkCircle $color="neutral-black" $width="2.4rem" $height="2.4rem" />
                    </Button.Tertiary>
                  </Container.Grid>
                </Container.Grid>

                <Container.Grid>
                  <Container.Grid $textAlign="right" style={{ height: '2.2rem' }}>
                    <Text.Body2>${(paymentCategoryTotals[PAYMENT_CATEGORY_MAP.gigPay] || 0).toFixed(2)}</Text.Body2>
                  </Container.Grid>
                  <Container.Grid $textAlign="right" style={{ height: '2.2rem', position: 'relative' }}>
                    <Text.Body2 $color={reconciliationPaymentsHeld ? 'neutral-dark' : 'neutral-black'}>
                      ${(paymentCategoryTotals[PAYMENT_CATEGORY_MAP.reconciliation] || 0).toFixed(2)}
                    </Text.Body2>
                    {reconciliationPaymentsHeld && (
                      <Text.Caption
                        $color="neutral-dark"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '0.1rem',
                        }}
                      >
                        <LocalizedText translationKey="dweb_block_payments_block_details_pending" />
                      </Text.Caption>
                    )}
                  </Container.Grid>
                </Container.Grid>
              </Container.Grid>

              {/* Tips and Reimbursements section */}
              <Container.Grid
                $gridTemplateColumns="3fr 1fr"
                $backgroundColor="neutral-white"
                $padding="1.6rem"
                $borderColor="neutral-light"
              >
                <Text.Body1 $color="neutral-black">
                  <LocalizedText translationKey="dweb_block_payments_block_details_total_tips" />
                </Text.Body1>
                <Text.Body1 $color="neutral-black" $textAlign="right">
                  ${(paymentCategoryTotals[PAYMENT_CATEGORY_MAP.tip] || 0).toFixed(2)}
                </Text.Body1>
              </Container.Grid>

              <Container.Grid
                $gridTemplateColumns="3fr 1fr"
                $backgroundColor="neutral-white"
                $padding="1.6rem"
                $borderColor="neutral-light"
              >
                <Text.Body1 $color="neutral-black">
                  <LocalizedText translationKey="dweb_block_payments_block_details_expense_reimbursement" />
                </Text.Body1>
                <Text.Body1 $color="neutral-black" $textAlign="right">
                  ${(paymentCategoryTotals[PAYMENT_CATEGORY_MAP.reimbursement] || 0).toFixed(2)}
                </Text.Body1>
              </Container.Grid>

              {/* Total section */}
              <Container.Grid
                $gridTemplateColumns="1fr 1fr"
                $alignItems="center"
                $backgroundColor={parsers.surfaceColor.modify(
                  'neutral-light',
                  parsers.baselineColor.modifiers.opacity(0.3)
                )}
                $padding="1rem 2.4rem"
                $borderColor="neutral-light"
              >
                <Container.Grid $justifyItems="flex-start">
                  <Text.Overline $color="neutral-black">
                    <LocalizedText translationKey="dweb_common_total" />
                  </Text.Overline>
                </Container.Grid>
                <Container.Grid $justifyItems="flex-end">
                  <Text.H5 $color="neutral-black" $fontWeight="bold">
                    ${totalAmount.toFixed(2)}
                  </Text.H5>
                </Container.Grid>
              </Container.Grid>
            </Container.Grid>
          </Container.Grid>
        </Container.Base>

        <Modal.Basic
          open={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          title={t('dweb_block_payments_block_details_pay_adjustment')}
          closeButtonText={t('dweb_common_close')}
        >
          <Container.Grid $gridGap="2.4rem">
            <Text.Body1 $color="neutral-black">
              <LocalizedText translationKey="dweb_block_details_pay_adjustment_explanation" />
            </Text.Body1>
          </Container.Grid>
        </Modal.Basic>
      </Panel>
      <Spacer $height="2.4rem" />
      <PanelTitle>
        <LocalizedText translationKey="dweb_block_payments_block_details_gig_information" />
      </PanelTitle>
      <Panel left noPadding>
        <Container.Base>
          {consolidations.length === 0 ? (
            <Container.Grid $padding="2.4rem" $textAlign="center">
              <Text.Body2 $color="neutral-dark">
                <LocalizedText translationKey="dweb_block_payments_block_details_no_gigs_were_completed_during_block" />
              </Text.Body2>
            </Container.Grid>
          ) : (
            consolidations.slice(0, consolidationsToShowIndex).map((consolidation) => (
              <Container.Grid
                key={consolidation.id}
                $borderStyle="solid"
                $borderWidth="1px 0 0 0"
                $borderColor="neutral-light"
              >
                <TransactionCell consolidation={consolidation} />
              </Container.Grid>
            ))
          )}
        </Container.Base>
      </Panel>
      {consolidations.length > consolidationsToShowIndex && (
        <>
          <Spacer $height="1.6rem" />
          <Container.Base style={{ display: 'flex', justifyContent: 'center' }}>
            <Button.Secondary onClick={() => setConsolidationsToShowIndex((prev) => prev + 5)} $width="100%">
              <LocalizedText translationKey="dweb_common_show_more" />
            </Button.Secondary>
          </Container.Base>
        </>
      )}
      <Spacer $height="6.4rem" />
      <SupportFooter helpText="NEED HELP WITH THIS BLOCK?" />
    </>
  )
}

type BlockDetailsProps = {
  match: {
    params: {
      block_id: string
    }
  }
}

const BlockDetailsPage: React.FC<BlockDetailsProps> = (props) => {
  const { t } = useLocalization()

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_block_payments_block_details')}</title>
      </Helmet>
      <BlockDetailsSection blockId={Number(props.match.params.block_id)} />
    </Page>
  )
}

export default BlockDetailsPage
